import React, { useEffect, useState } from 'react';
import { adminService, User } from '../services/adminService';
import './AdminPanel.css';

const AdminPanel: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved'>('all');

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const allUsers = await adminService.getAllUsers();
      setUsers(allUsers);
      setPendingCount(allUsers.filter(u => !u.isApproved).length);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load users');
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleApprove = async (userId: string) => {
    if (!window.confirm('Approve this user?')) return;
    
    try {
      await adminService.approveUser(userId);
      await loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to approve user');
    }
  };

  const handleRevoke = async (userId: string) => {
    if (!window.confirm('Revoke approval for this user?')) return;
    
    try {
      await adminService.revokeUser(userId);
      await loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to revoke user');
    }
  };

  const handleDelete = async (userId: string) => {
    if (!window.confirm('Delete this user? This action cannot be undone.')) return;
    
    try {
      await adminService.deleteUser(userId);
      await loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleRoleChange = async (userId: string, newRole: 'user' | 'admin') => {
    if (!window.confirm(`Change user role to ${newRole}?`)) return;
    
    try {
      await adminService.updateUserRole(userId, newRole);
      await loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to update role');
    }
  };

  const filteredUsers = users.filter(user => {
    if (filter === 'pending') return !user.isApproved;
    if (filter === 'approved') return user.isApproved;
    return true;
  });

  if (loading) {
    return <div className="admin-panel"><p>Loading users...</p></div>;
  }

  if (error) {
    return (
      <div className="admin-panel">
        <div className="error-message">{error}</div>
        <button onClick={loadUsers}>Retry</button>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h1>User Management</h1>
        <div className="admin-stats">
          <span className="stat-badge pending">{pendingCount} Pending</span>
          <span className="stat-badge approved">{users.length - pendingCount} Approved</span>
          <span className="stat-badge total">{users.length} Total</span>
        </div>
      </div>

      <div className="filter-tabs">
        <button 
          className={filter === 'all' ? 'active' : ''} 
          onClick={() => setFilter('all')}
        >
          All Users ({users.length})
        </button>
        <button 
          className={filter === 'pending' ? 'active' : ''} 
          onClick={() => setFilter('pending')}
        >
          Pending ({pendingCount})
        </button>
        <button 
          className={filter === 'approved' ? 'active' : ''} 
          onClick={() => setFilter('approved')}
        >
          Approved ({users.length - pendingCount})
        </button>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Registered</th>
              <th>Last Login</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={7} className="no-users">No users found</td>
              </tr>
            ) : (
              filteredUsers.map(user => (
                <tr key={user.id} className={!user.isApproved ? 'pending-user' : ''}>
                  <td className="username-cell">
                    <strong>{user.username}</strong>
                  </td>
                  <td>{user.email || <span className="no-email">—</span>}</td>
                  <td>
                    <select 
                      value={user.role} 
                      onChange={(e) => handleRoleChange(user.id, e.target.value as 'user' | 'admin')}
                      className={`role-select ${user.role}`}
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td>
                    {user.isApproved ? (
                      <span className="status-badge approved">✓ Approved</span>
                    ) : (
                      <span className="status-badge pending">⏳ Pending</span>
                    )}
                  </td>
                  <td className="date-cell">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="date-cell">
                    {user.lastLogin 
                      ? new Date(user.lastLogin).toLocaleDateString()
                      : <span className="no-login">Never</span>
                    }
                  </td>
                  <td className="actions-cell">
                    {!user.isApproved ? (
                      <button 
                        className="btn-approve" 
                        onClick={() => handleApprove(user.id)}
                      >
                        ✓ Approve
                      </button>
                    ) : (
                      <button 
                        className="btn-revoke" 
                        onClick={() => handleRevoke(user.id)}
                      >
                        ✗ Revoke
                      </button>
                    )}
                    <button 
                      className="btn-delete" 
                      onClick={() => handleDelete(user.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPanel;
